import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import {
  CeHostDialogComponent,
  CeHostDialogData,
} from '../../../shared/components/ce-host-dialog/ce-host-dialog.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { IssueManagementService } from '../issue-management/issue-management.service';

interface IssueRecord {
  issue_id: number | string;
  issue_short_desc?: string;
  issue_desc?: string;
  qar_desc?: string;
  tax_year?: string | number;
  scenario?: string | number;
}

interface IssueTableRow {
  issue_id: number | string;
  issue_short_desc: string;
  issue_desc: string;
  qar_desc: string;
  tax_year: string | number;
  scenario: string | number;
}

type IssueSortField = keyof IssueTableRow;

@Component({
  selector: 'gtw-issue-management-list',
  templateUrl: './issue-management-list.component.html',
  styleUrls: ['./issue-management-list.component.scss'],
  standalone: false,
})
export class IssueManagementListComponent implements OnInit {
  readonly requestGridUpdate = output<string>({ alias: 'request-grid-update' });

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly issueService = inject(IssueManagementService);
  private readonly gtwGlobal = inject(GlobalService);
  private readonly gtwWorkspace = inject(WorkspaceService);
  private readonly gtwApp = inject(AppService);
  private readonly snackbar = inject(GtwSnackbarService);
  private readonly dialog = inject(MatDialog);

  private isInitialized = false;
  private isLoading = false;

  baseURLs: Record<string, string> = {};
  globalService: Record<string, unknown> = {};
  GENERAL_CONFIG: { base_url?: string } = {};
  filterParams: Record<string, unknown> = {};
  headerData: Record<string, unknown> = {};

  readonly loading = signal(false);
  readonly searchTerm = signal('');
  readonly data = signal<IssueRecord[]>([]);
  readonly tableRows = signal<IssueTableRow[]>([]);
  readonly sortField = signal<IssueSortField | null>(null);
  readonly sortDirection = signal<'asc' | 'desc'>('asc');

  taxYears: unknown[] = [];
  scenarios: unknown[] = [];
  filterTaxYear: { value?: string } | null = null;
  filterScenario: unknown = null;

  adjTypeOptions: unknown[] = [];
  readonly multiYearDropDownOptions = [
    { value: 'Multi-year', desc: 'Multi-year' },
    { value: 'More than one line', desc: 'More than one line' },
  ];
  readonly timingOptions = [
    { value: 'Perm', desc: 'Perm' },
    { value: 'Short Term Temp (1-3)', desc: 'Short Term Temp (1-3)' },
    { value: 'Long term temp (3-10)', desc: 'Long term temp (3-10)' },
    { value: 'Others', desc: 'Others' },
  ];

  private activeScreen: { data?: Record<string, unknown> } | null = null;
  private issuesMgmtCache: Record<string, unknown> = {};

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const urls = JSON.parse(v || '{}') as Record<string, string>;
      if (Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch {
      console.log('base-urls input not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = JSON.parse(v || '{}') as {
        globalParams?: Record<string, unknown>;
        generalConfigData?: { base_url?: string };
      };
      if (Object.keys(header).length > 0) {
        this.globalService = header.globalParams || {};
        this.GENERAL_CONFIG = header.generalConfigData || {};
        this.headerData = header as Record<string, unknown>;
        this.tryInit();
      }
    } catch {
      console.log('header-data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      this.filterParams = JSON.parse(v || '{}') as Record<string, unknown>;
      this.tryInit();
    } catch {
      console.log('filter-params input not yet stable.');
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.restoreIssuesCache();

    this.gtwWorkspace.setTabActive
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyWorkspaceContext();
        this.restoreIssuesCache();
        if (this.isInitialized) {
          this.rebuildTableRows();
        } else {
          this.tryInit();
        }
      });

    this.gtwGlobal.globalParamsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyWorkspaceContext();
        if (this.isInitialized) {
          this.rebuildTableRows();
        } else {
          this.tryInit();
        }
      });

    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.activeScreen = activeScreen;
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    if (activeScreen && Object.keys(activeScreen).length > 0) {
      this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
      this.globalService = header.globalParams || {};
      this.GENERAL_CONFIG = header.generalConfigData || {};
      this.headerData = header;
      this.filterParams = normalizeFilterParams(activeScreen);
      this.issuesMgmtCache = (activeScreen.data || {}) as Record<string, unknown>;
    }

    this.taxYears = this.gtwGlobal.global?.inputs?.tax_year?.values || [];
    this.filterTaxYear =
      (this.issuesMgmtCache['taxYear'] as { value?: string } | null) ||
      this.gtwGlobal.global?.inputs?.tax_year?.selected ||
      null;
    this.filterScenario = this.issuesMgmtCache['scenario'] || null;
    this.scenarios = (this.issuesMgmtCache['scenarios'] as unknown[]) || [];
    const cachedData = this.issuesMgmtCache['data'];
    this.data.set(Array.isArray(cachedData) ? (cachedData as IssueRecord[]) : this.data());

    this.cdr.markForCheck();
  }

  private restoreIssuesCache(): void {
    const cache = this.activeScreen?.data;
    if (!cache) {
      return;
    }
    if (Array.isArray(cache['data'])) {
      this.data.set(cache['data'] as IssueRecord[]);
      this.rebuildTableRows();
    }
    if (Array.isArray(cache['scenarios'])) {
      this.scenarios = cache['scenarios'] as unknown[];
    }
    if (cache['taxYear']) {
      this.filterTaxYear = cache['taxYear'] as { value?: string };
    }
    if (cache['scenario']) {
      this.filterScenario = cache['scenario'];
    }
    if (this.data().length > 0) {
      this.isInitialized = true;
    }
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) {
      return;
    }
    this.isInitialized = true;
    this.loadAdjustmentTypes();
    if (this.data().length === 0) {
      this.getData();
    } else {
      this.rebuildTableRows();
    }
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.['api'] || '';
  }

  private cacheIssuesState(): void {
    if (!this.activeScreen) {
      return;
    }
    this.issuesMgmtCache = {
      ...this.issuesMgmtCache,
      data: this.data(),
      scenarios: this.scenarios,
      taxYear: this.filterTaxYear,
      scenario: this.filterScenario,
    };
    this.activeScreen.data = {
      ...(this.activeScreen.data || {}),
      ...this.issuesMgmtCache,
    };
    this.gtwWorkspace.setCache(this.activeScreen, this.issuesMgmtCache);
  }

  loadAdjustmentTypes(): void {
    const taxYear = this.filterTaxYear?.value;
    if (!taxYear || !this.baseUrl) {
      return;
    }

    this.issueService
      .getAdjustmentTypes(this.baseUrl, taxYear)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { callSuccess?: string; jsonObject?: unknown[] }) => {
          if (response?.callSuccess === '1') {
            this.adjTypeOptions = [...(response.jsonObject || [])];
            this.adjTypeOptions.push({
              CODE_DESC: 'Other (specify in the explanation below)',
            });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackbar.openSnackBar(
            'Something went wrong while loading adjustment types for the given tax year, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.markForCheck();
        },
      });
  }

  getData(): void {
    if (!this.baseUrl || this.isLoading) {
      return;
    }

    this.loading.set(true);
    this.isLoading = true;
    this.cdr.markForCheck();

    this.issueService
      .getAllIssues(this.baseUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { callSuccess?: string; issue?: IssueRecord[] }) => {
          if (response?.callSuccess === '1') {
            this.data.set(response.issue || []);
            this.cacheIssuesState();
            this.rebuildTableRows();
          } else {
            this.data.set([]);
            this.tableRows.set([]);
            this.cacheIssuesState();
            this.snackbar.openSnackBar(
              'Error occurred fetching data. If this continues please contact support',
              'gtw-snackbar--red'
            );
          }
        },
        error: () => {
          this.snackbar.openSnackBar(
            'Error occurred fetching data. If this continues please contact support',
            'gtw-snackbar--red'
          );
        },
        complete: () => {
          this.loading.set(false);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onSearchInput(event: Event): void {
    const target = event.target;
    const term = target instanceof HTMLInputElement ? target.value : '';
    this.onSearchChange(term);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term || '');
    this.rebuildTableRows();
  }

  clearSearch(input: HTMLInputElement): void {
    input.value = '';
    this.searchTerm.set('');
    this.rebuildTableRows();
  }

  sortBy(field: IssueSortField): void {
    if (this.sortField() === field) {
      this.sortDirection.update((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortField.set(field);
      this.sortDirection.set('asc');
    }
    this.rebuildTableRows();
  }

  ariaSort(field: IssueSortField): 'ascending' | 'descending' | 'none' {
    if (this.sortField() !== field) {
      return 'none';
    }
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  private rebuildTableRows(): void {
    const term = (this.searchTerm() || '').trim().toLowerCase();
    let rows = [...this.data()];

    if (term) {
      rows = rows.filter((row) => this.rowMatchesSearch(row, term));
    }

    let tableRows = rows.map((row) => ({
      issue_id: row.issue_id,
      issue_short_desc: row.issue_short_desc || '',
      issue_desc: row.issue_desc || '',
      qar_desc: row.qar_desc || '',
      tax_year: row.tax_year ?? '',
      scenario: row.scenario ?? '',
    }));

    const field = this.sortField();
    if (field) {
      const direction = this.sortDirection() === 'asc' ? 1 : -1;
      tableRows = tableRows.sort((left, right) => {
        return String(left[field] ?? '').localeCompare(String(right[field] ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * direction;
      });
    }

    this.tableRows.set(tableRows);
    this.cdr.markForCheck();
  }

  private rowMatchesSearch(row: IssueRecord, term: string): boolean {
    const values = [
      row.issue_id,
      row.issue_short_desc,
      row.issue_desc,
      row.qar_desc,
      row.tax_year,
      row.scenario,
    ];
    return values.some((value) => String(value ?? '').toLowerCase().includes(term));
  }

  addIssue(): void {
    this.openIssueDialog('add', {});
  }

  editIssue(row: IssueTableRow): void {
    this.openIssueDialog('edit', row);
  }

  private openIssueDialog(mode: 'add' | 'edit', rowData: IssueTableRow | Record<string, never>): void {
    const modalPayload = {
      mode,
      taxYears: this.taxYears,
      selectedTaxYear: this.filterTaxYear,
      selectedScenario: this.filterScenario,
      issuesData: this.data(),
      multiYearDropDownOptions: this.multiYearDropDownOptions,
      adjTypeOptions: this.adjTypeOptions,
      timingOptions: this.timingOptions,
    };

    const dialogData: CeHostDialogData = {
      ceTagName: mode === 'add' ? 'gtw-issue-add-ce' : 'gtw-issue-edit-ce',
      rowData: mode === 'edit' ? rowData : {},
      colData: {},
      headerData: this.headerData,
      baseUrls: {
        ...this.baseURLs,
        base_url: this.baseUrl,
      },
      filterParams: this.filterParams,
      extraAttributes: {
        'modal-data': modalPayload,
      },
    };

    const dialogRef = this.dialog.open(CeHostDialogComponent, {
      data: dialogData,
      width: mode === 'add' ? '60vw' : '90vw',
      maxWidth: '95vw',
      panelClass: ['ce-host-dialog-panel', 'ce-host-dialog-issue-mgmt'],
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { action?: string } | undefined) => {
        if (result?.action === 'refresh') {
          this.getData();
        }
      });
  }
}
